import {useTheme} from '@primer/react-brand/esm'
import {useEffect, useRef, type SyntheticEvent} from 'react'
import {usePrefersReducedMotion} from '@github-ui/use-prefers-reduced-motion'

import type {HeroSequenceProps} from './types'

export function useHeroSequencePlayback({
  video,
  currentChapterIndex,
  onNextChapter,
  onReady,
  onPlaybackChange,
  videoRef,
  seekRequest,
}: Omit<HeroSequenceProps, 'chaptersAltDesc'>) {
  const {colorMode} = useTheme()
  const prefersReducedMotion = usePrefersReducedMotion()
  const videoUrl = colorMode === 'dark' ? video?.url.dark || video?.url.light : video?.url.light
  const loadedSourceRef = useRef<string | null>(null)
  const reportedChapterRef = useRef(currentChapterIndex)
  const appliedSeekRequestRef = useRef(seekRequest)
  const positionRef = useRef<{currentTime: number; isPaused: boolean; isFinished: boolean} | null>(null)
  const isPlayingRef = useRef(false)

  const reportPlayback = (event: SyntheticEvent<HTMLVideoElement>) => {
    const media = event.currentTarget
    if (loadedSourceRef.current !== media.getAttribute('src')) return

    if (event.type === 'playing') isPlayingRef.current = true
    if (['pause', 'waiting', 'seeking', 'ended', 'error'].includes(event.type)) isPlayingRef.current = false

    const state = {
      currentTime: media.currentTime,
      isPaused: media.paused,
      isPlaying: isPlayingRef.current && !media.paused && !media.ended,
      isFinished: media.ended,
    }
    positionRef.current = state
    onPlaybackChange(state)

    const chapterIndex = video?.chapterTimestamps.findLastIndex(timestamp => timestamp <= media.currentTime) ?? -1
    if (chapterIndex >= 0 && chapterIndex !== reportedChapterRef.current) {
      reportedChapterRef.current = chapterIndex
      onNextChapter(chapterIndex)
    }
  }

  const handleMetadata = async (event: SyntheticEvent<HTMLVideoElement>) => {
    const media = event.currentTarget
    const timestamps = video?.chapterTimestamps
    if (!timestamps) return
    if (
      !Number.isFinite(media.duration) ||
      timestamps.some(
        (timestamp, index) =>
          !Number.isFinite(timestamp) ||
          timestamp < 0 ||
          timestamp >= media.duration ||
          timestamp <= (timestamps[index - 1] ?? -1),
      )
    ) {
      window.reportError(new Error('Hero video chapter timestamps must be increasing and within the video duration.'))
      return
    }

    const previousPosition = positionRef.current
    const hasPendingSeek =
      appliedSeekRequestRef.current !== seekRequest || reportedChapterRef.current !== currentChapterIndex
    let currentTime = timestamps[currentChapterIndex] ?? 0
    if (!hasPendingSeek && previousPosition) {
      currentTime = previousPosition.isFinished ? media.duration : previousPosition.currentTime
    }
    loadedSourceRef.current = media.getAttribute('src')
    appliedSeekRequestRef.current = seekRequest
    reportedChapterRef.current = currentChapterIndex
    media.currentTime = Math.min(currentTime, media.duration)
    onReady(timestamps.map((timestamp, index) => (timestamps[index + 1] ?? media.duration) - timestamp))
    reportPlayback(event)

    if (previousPosition ? !previousPosition.isPaused && !previousPosition.isFinished : !prefersReducedMotion) {
      try {
        await media.play()
      } catch (error) {
        // Source changes and a pause during loading can cancel an outstanding play request.
        if (error instanceof DOMException && error.name === 'AbortError') return
        window.reportError(error)
      }
    }
  }

  useEffect(() => {
    const media = videoRef.current
    if (!media || loadedSourceRef.current !== videoUrl) return
    if (appliedSeekRequestRef.current === seekRequest && reportedChapterRef.current === currentChapterIndex) return

    const timestamp = video?.chapterTimestamps[currentChapterIndex]
    if (timestamp === undefined) {
      window.reportError(new Error(`Hero video has no timestamp for chapter ${currentChapterIndex}.`))
      return
    }
    appliedSeekRequestRef.current = seekRequest
    reportedChapterRef.current = currentChapterIndex
    media.currentTime = timestamp
  }, [currentChapterIndex, seekRequest, video, videoRef, videoUrl])

  useEffect(() => {
    if (prefersReducedMotion) videoRef.current?.pause()
  }, [prefersReducedMotion, videoRef])

  const handleError = (event: SyntheticEvent<HTMLVideoElement>) => {
    const media = event.currentTarget
    media.pause()
    isPlayingRef.current = false
    const state = {
      currentTime: positionRef.current?.currentTime ?? media.currentTime,
      isPaused: true,
      isPlaying: false,
      isFinished: false,
    }
    positionRef.current = state
    onPlaybackChange(state)
    window.reportError(new Error(`Hero video failed to load (media error ${media.error?.code}).`))
  }

  return {videoUrl, handleMetadata, reportPlayback, handleError}
}
